import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Sugargoo Shipping Cost Explained: Cheapest Methods Compared (2026)",
  description: "Complete breakdown of Sugargoo international shipping costs, carrier options, delivery times, and money-saving strategies for dropshippers and international buyers.",
  image: "https://readdy.ai/api/search-image?query=Professional%20logistics%20and%20shipping%20concept%20photography%20cardboard%20boxes%20packages%20with%20shipping%20labels%20on%20clean%20white%20desk%20natural%20light%20minimal%20workspace%20modern%20warehouse%20aesthetic%20well%20organized%20parcels%20warm%20editorial%20style&width=1400&height=700&seq=sugargoo-shipping-cost&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  publisher: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  inLanguage: "en",
  isPartOf: { "@type": "WebSite", name: "Sugargoo Spreadsheet", url: siteUrl },
};

export default function ShippingCostGuide() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Sugargoo Shipping Cost Guide</span>
            </nav>

            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">Shipping</div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Sugargoo Shipping Cost Explained: Cheapest Methods Compared (2026)
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                Complete breakdown of Sugargoo international shipping costs, carrier options, delivery times, and money-saving strategies for dropshippers and international buyers.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated: August 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 7 min read</span>
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Professional%20logistics%20and%20shipping%20concept%20photography%20cardboard%20boxes%20packages%20with%20shipping%20labels%20on%20clean%20white%20desk%20natural%20light%20minimal%20workspace%20modern%20warehouse%20aesthetic%20well%20organized%20parcels%20warm%20editorial%20style&width=1400&height=700&seq=sugargoo-shipping-cost&orientation=landscape" alt="Sugargoo Shipping Cost Guide - International Shipping Methods" className="w-full h-full object-cover" />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Understanding Sugargoo Shipping</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Shipping is one of the most important — and often most confusing — aspects of using Sugargoo. Whether you are sourcing products found in your <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link> or browsing independently, understanding the shipping options, costs, and timelines can save you significant money and prevent frustrating delays.
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Sugargoo offers multiple international shipping carriers, each with different pricing structures, delivery speeds, and service levels. The key to minimizing costs is choosing the right carrier for your specific package size, weight, destination, and urgency requirements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Shipping Carrier Comparison</h2>
              <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-900">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Carrier</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Speed</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Cost Level</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Best For</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["DHL Express","3-7 days","High","Urgent orders, high-value items"],
                      ["FedEx","4-8 days","High","Reliable express delivery"],
                      ["EMS","7-15 days","Medium","Balanced speed and cost"],
                      ["E-EMS","7-20 days","Medium-Low","Cost-effective express"],
                      ["SAL","15-30 days","Low","Budget-friendly medium parcels"],
                      ["China Post Air Mail","15-40 days","Very Low","Small, non-urgent items"],
                      ["Sea Mail","30-60 days","Lowest","Large, heavy shipments"],
                      ["Railway","20-35 days","Low","European destinations"],
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/70 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
                        <td className="px-4 py-3 text-xs font-medium text-foreground-900">{row[0]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[1]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[2]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How to Reduce Shipping Costs</h2>
              <div className="space-y-3">
                {[
                  { title: "Consolidate Packages", desc: "Instead of shipping items individually, use Sugargoo's free warehouse storage to consolidate multiple orders into one package. This significantly reduces per-item shipping costs." },
                  { title: "Choose Economy Lines", desc: "If you are not in a hurry, economy shipping lines like SAL and China Post Air Mail can reduce costs by 40-60% compared to express carriers. The trade-off is longer delivery times." },
                  { title: "Use Rehearsal Shipping", desc: "Sugargoo's rehearsal shipping service weighs and measures your package before creating the final label. This prevents overestimation and ensures you pay for exact weight, not estimates." },
                  { title: "Remove Unnecessary Packaging", desc: "Request to remove shoeboxes, retail packaging, or excess wrapping. This reduces both weight and volumetric size, which directly lowers shipping costs." },
                  { title: "Ship During Off-Peak Periods", desc: "Avoid major Chinese holidays like Chinese New Year and Singles Day (11.11) when shipping rates spike due to overwhelming demand across all carriers." },
                ].map((tip, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50 mt-0.5">
                      <span className="text-xs font-bold text-primary-500">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{tip.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Shipping Cost Estimates by Weight</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                While exact costs vary by destination and current fuel surcharges, here are approximate shipping costs from Sugargoo warehouse to major destinations based on package weight.
              </p>
              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-900">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">Weight</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">DHL (USA)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">EMS (Europe)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-background-200">SAL (Worldwide)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["0.5 kg","$15-20","$12-18","$8-12"],
                      ["1 kg","$20-28","$18-25","$12-18"],
                      ["2 kg","$30-40","$28-35","$18-25"],
                      ["5 kg","$55-70","$50-65","$30-45"],
                      ["10 kg","$90-120","$80-100","$50-70"],
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/70 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
                        <td className="px-4 py-3 text-xs font-medium text-foreground-900">{row[0]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[1]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[2]}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-foreground-400 mt-3 italic">Note: Prices are estimates as of August 2026. Actual costs may vary based on volumetric weight, fuel surcharges, and destination country customs policies.</p>
            </section>

            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">Calculate Your Exact Shipping Cost</h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">Pair the Sugargoo Spreadsheet with smart shipping choices for maximum savings on every order.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">Get Sugargoo Spreadsheet</a>
                <Link to="/" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap">Back to Homepage</Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li><Link to="/blog/sugargoo-review-2026" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Review 2026 — Complete Platform Analysis</Link></li>
                <li><Link to="/blog/sugargoo-warehouse-guide" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Warehouse Guide — Storage, QC & Processing</Link></li>
                <li><Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Homepage — Browse All Categories</Link></li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}